import type { FC } from "react";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import type { TunnistusState } from "../../store/auth";
import {
  authRedirect,
  fetchUserCompletion,
  getCompletionFetchState,
  getCompletions,
  getTunnistusData,
} from "../../store/auth";
import { useSelector } from "react-redux";
import type { AccessTokenResponse } from "../../@api/signupUser";
import {
  css,
  FullPageLoader,
  Margin,
  useIsSmallScreenSize,
} from "@kivra/react-components";
import type { BECompletionV4 } from "@kivra/sdk/types/core/completion";
import { Completion as Completions } from "../completion/Completion";
import { ErrorMessage } from "../errors/ErrorMessage";
import { KivraHeaderRouter } from "../header/KivraHeaderRouter";
import { getRedirectUri } from "../../@api/config";
import type { RootState } from "../../store/rootReducer";
import { routes } from "../../utils/routes";
import type { FetchState } from "../../utils/use-completions";
import { useAppDispatch } from "../../store/configureStore";
import { getQueryValue } from "@kivra/sdk/common";

export const Completion: FC = () => {
  const history = useHistory();
  const { completions, fetchState, tunnistusData, redirectUri } = useSelector<
    RootState,
    CompletionsProps
  >(getCompletionsState);
  const { fetchCompletions, doAuthRedirect } = useCompletionActions();
  const isSmallScreenSize = useIsSmallScreenSize();
  // TODO: This might be incorrect, do we even use this here?
  const state = getQueryValue("state", window.location.href) ?? undefined;

  const hasRegistrationCompletions = completions.some(
    (c) => c.context === "registration",
  );

  // We should only get to the Completion page after logging in.
  if (!tunnistusData.accessToken) {
    history.replace(routes.ROOT);
    return null;
  }

  if (fetchState === "FETCHING") {
    return <FullPageLoader />;
  }

  if (fetchState === "INITIAL") {
    fetchCompletions();
    return <FullPageLoader />;
  }

  if (fetchState === "FETCHED" && completions.length === 0) {
    doAuthRedirect({
      resource_owner: tunnistusData.resourceOwner,
      access_token: tunnistusData.accessToken,
      state,
    });

    return <FullPageLoader />;
  }

  return (
    <>
      {!isSmallScreenSize && <BackgroundIllustration />}

      <KivraHeaderRouter />
      <ErrorMessage />
      <Margin bottom={46} />
      <div
        className={css({
          width: "100%",
          display: "flex",
          zIndex: 0,
          justifyContent: "center",
          marginTop: "$spacing-32",
        })}
      >
        {hasRegistrationCompletions
          ? (
            // We need to handle if a user has unfinished registration
            // completions in a separate way than "normal" unfinished
            // completions.
            <CompletionsWithRegistration
              completions={completions}
              fetchState={fetchState}
              tunnistusData={tunnistusData}
              redirectUri={redirectUri}
            />
          )
          : (
            <Completions
              completions={completions}
              onCompletionSuccessful={() => {
                doAuthRedirect({
                  resource_owner: tunnistusData.resourceOwner,
                  access_token: tunnistusData.accessToken,
                  // TODO: not sure if the state here is valid
                  state,
                });
              }}
            />
          )}
      </div>
    </>
  );
};

interface CompletionsProps {
  completions: BECompletionV4[];
  fetchState: FetchState;
  tunnistusData: TunnistusState;
  redirectUri: string;
}

const getCompletionsState = (state: RootState): CompletionsProps => ({
  completions: getCompletions(state).filter(
    // We should never get settings here,
    // but it's a possible context.
    // So we filter it out in case.
    (completion) => completion.context !== "settings",
  ),
  fetchState: getCompletionFetchState(state),
  tunnistusData: getTunnistusData(state),
  redirectUri: getRedirectUri(), // TODO: This is probably not correct
});

interface CompletionActions {
  fetchCompletions: () => void;
  doAuthRedirect: (response: AccessTokenResponse) => void;
}

const useCompletionActions = (): CompletionActions => {
  const dispatch = useAppDispatch();
  return {
    fetchCompletions: () => dispatch(fetchUserCompletion()),
    doAuthRedirect: (response: AccessTokenResponse) =>
      dispatch(authRedirect(response)),
  };
};

function CompletionsWithRegistration(props: CompletionsProps) {
  const [currentStep, setCurrentStep] = useState(0);
  // TODO: This might be incorrect
  const history = useHistory();

  return (
    <div className={css({ display: "flex", flexDirection: "column" })}>
      {
        // TODO: This seems to be unnecessary for us
        // <RegistrationProgressBar /** Add the component or remove */
        //     currentStep={props.completions[currentStep].type}
        // />
      }

      <Completions
        completions={props.completions}
        onPartCompletionSuccessful={() => {
          setCurrentStep(currentStep + 1);
        }}
        onCompletionSuccessful={() => {
          // TODO: This might need to be authRedirect?
          // How's the signup return actually handled?
          history.push(routes.SIGNUP_RETURN, {
            // TODO: This probably doesn't work
            // Is this correct? Do the params work correctly?
            accessToken: props.tunnistusData.accessToken,
            redirectUri: props.redirectUri,
          });
        }}
      />
    </div>
  );
}

function BackgroundIllustration() {
  return (
    <div
      aria-hidden
      style={{
        zIndex: 0,
        position: "fixed",
        bottom: 24,
        right: 24,
      }}
    >
      {
        /**
             * ! Something is wonky here
             * TODO: Check if this can be added back?
            <Illustration variant="onboarding.registration" size="medium" />
             */
      }
    </div>
  );
}
